import { UTCDate } from '@date-fns/utc';
import { Currency } from '@seapoint/server/enums';
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInQuarters,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { AccountView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { isInternalTransfer } from './is-internal-transfer';
import { isRefund } from './is-refund';

export type TimePeriod = 'day' | 'week' | 'month' | 'quarter' | 'year';

export type MoneyMovementPeriodData = {
  period: string; // Display label (e.g., "Jan 2024", "Week 1", "Q1 2024")
  moneyIn: number; // Total positive cash flow for this period
  moneyOut: number; // Total negative cash flow for this period (as positive number)
  periodStart: Date; // Start date of this period
  periodEnd: Date; // End date of this period
  date: string; // ISO string of period start for sorting/reference
};

type GetMoneyMovementDataParams = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency | Currency;
  startDate: Date;
  endDate: Date;
  timePeriod: TimePeriod;
};

type PeriodHelpers = {
  getDifference: (end: Date, start: Date) => number;
  getStartOf: (date: Date) => Date;
  getEndOf: (date: Date) => Date;
  addPeriod: (date: Date, amount: number) => Date;
  formatPeriod: (date: Date) => string;
};

// Helper to get functions for different time periods
const getPeriodHelpers = (timePeriod: TimePeriod): PeriodHelpers => {
  switch (timePeriod) {
    case 'day':
      return {
        getDifference: (end, start) => differenceInDays(end, start),
        getStartOf: (date) => startOfDay(date),
        getEndOf: (date) => endOfDay(date),
        addPeriod: (date, amount) => addDays(date, amount),
        formatPeriod: (date: Date) => format(date, 'MMM d'),
      };
    case 'week':
      return {
        getDifference: (end, start) => differenceInWeeks(end, start),
        getStartOf: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
        getEndOf: (date: Date) => endOfWeek(date, { weekStartsOn: 1 }),
        addPeriod: (date, amount) => addWeeks(date, amount),
        formatPeriod: (date: Date) => `Week of ${format(date, 'MMM d')}`,
      };
    case 'month':
      return {
        getDifference: (end, start) => differenceInMonths(end, start),
        getStartOf: (date) => startOfMonth(date),
        getEndOf: (date) => endOfMonth(date),
        addPeriod: (date, amount) => addMonths(date, amount),
        formatPeriod: (date: Date) => format(date, "MMM ''yy"),
      };
    case 'quarter':
      return {
        getDifference: (end, start) => differenceInQuarters(end, start),
        getStartOf: (date) => startOfQuarter(date),
        getEndOf: (date) => endOfQuarter(date),
        addPeriod: (date, amount) => addQuarters(date, amount),
        formatPeriod: (date: Date) => `Q${Math.ceil((date.getMonth() + 1) / 3)} ${format(date, 'yyyy')}`,
      };
    case 'year':
      return {
        getDifference: (end, start) => differenceInYears(end, start),
        getStartOf: (date) => startOfYear(date),
        getEndOf: (date) => endOfYear(date),
        addPeriod: (date, amount) => addYears(date, amount),
        formatPeriod: (date: Date) => format(date, 'yyyy'),
      };
    default:
      return exhaustiveCheck(timePeriod);
  }
};

// Helper to format a single period when the date range is smaller than the requested period
const formatSinglePeriod = (startDate: Date, endDate: Date): string => {
  if (isSameDay(startDate, endDate)) {
    return format(startDate, 'MMM d, yyyy');
  }

  const startFormatted = format(startDate, 'MMM d');
  const endFormatted = format(endDate, 'MMM d, yyyy');

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Generates money movement data (in/out) for specified time periods within a date range.
 *
 * @param accounts - Array of account data containing transactions
 * @param baseCurrency - Base currency for calculations (reserved for future use)
 * @param startDate - Start of the analysis period
 * @param endDate - End of the analysis period
 * @param timePeriod - Granularity: 'day', 'week', 'month', 'quarter', 'year'
 * @returns Array of money movement data for each period
 */

export const getMoneyMovementData = ({
  accounts,
  startDate,
  endDate,
  timePeriod,
}: GetMoneyMovementDataParams): MoneyMovementPeriodData[] => {
  // Validate inputs
  if (!accounts || accounts.length === 0) {
    return [];
  }

  if (isAfter(startDate, endDate)) {
    return [];
  }

  // Ensure endDate is end of day if it's just a date
  const adjustedEndDate = endOfDay(endDate);

  // Collect all transactions from all accounts within the date range, including refunds
  const allTransactions = accounts.flatMap((account) =>
    (account.transactions || [])
      .filter((transaction) => {
        if (!transaction.fspDate) return false;
        const transactionDate = new UTCDate(transaction.fspDate);
        return isWithinInterval(transactionDate, { start: startDate, end: adjustedEndDate });
      })
      .filter((transaction) => !isInternalTransfer(transaction))
      .map((transaction) => ({
        date: new UTCDate(transaction.fspDate!),
        baseCurrencyAmount: transaction.baseCurrencyAmount || 0,
        isCredit: transaction.isCredit || false,
        vendor: transaction.vendor,
        type: transaction.type,
      })),
  );

  // Get the appropriate difference function and period generators
  const { getDifference, getStartOf, getEndOf, addPeriod, formatPeriod } = getPeriodHelpers(timePeriod);

  // Calculate the number of periods needed
  const totalPeriods = getDifference(adjustedEndDate, startDate);

  // Handle edge case: if the date range is smaller than the requested period
  if (totalPeriods === 0) {
    // Return a single period covering the entire range
    // Filter transactions to only include those within the exact date range
    const rangeTransactions = allTransactions.filter((transaction) =>
      isWithinInterval(transaction.date, { start: startDate, end: adjustedEndDate }),
    );

    const moneyIn = rangeTransactions
      .filter((t) => t.isCredit && !isRefund(t.type))
      .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0);

    const moneyOut =
      rangeTransactions.filter((t) => !t.isCredit).reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0) -
      // Subtract refunds from moneyOut to reduce burn rate
      rangeTransactions
        .filter((t) => t.isCredit && isRefund(t.type))
        .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0);

    return [
      {
        period: formatSinglePeriod(startDate, endDate),
        moneyIn,
        moneyOut,
        periodStart: startDate,
        periodEnd: adjustedEndDate,
        date: startDate.toISOString(),
      },
    ];
  }

  // Generate periods and calculate money movement for each
  const periods: MoneyMovementPeriodData[] = [];

  for (let i = 0; i <= totalPeriods; i++) {
    const periodStart = getStartOf(addPeriod(getStartOf(startDate), i));
    const periodEnd = getEndOf(addPeriod(getStartOf(startDate), i));

    // Ensure we don't go beyond the requested end date
    const actualPeriodEnd = isBefore(periodEnd, adjustedEndDate) ? periodEnd : adjustedEndDate;

    // Filter transactions for this period
    const periodTransactions = allTransactions.filter((transaction) =>
      isWithinInterval(transaction.date, { start: periodStart, end: actualPeriodEnd }),
    );

    // Calculate money in (credits) and money out (debits)
    // For refunds: add them back to moneyOut to reduce burn rate
    const moneyIn = periodTransactions
      .filter((t) => t.isCredit && !isRefund(t.type))
      .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0);

    const moneyOut =
      periodTransactions.filter((t) => !t.isCredit).reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0) -
      // Subtract refunds from moneyOut to reduce burn rate
      periodTransactions
        .filter((t) => t.isCredit && isRefund(t.type))
        .reduce((sum, t) => sum + Math.abs(t.baseCurrencyAmount), 0);

    periods.push({
      period: formatPeriod(periodStart),
      moneyIn,
      moneyOut,
      periodStart,
      periodEnd: actualPeriodEnd,
      date: periodStart.toISOString(),
    });

    // Stop if we've reached the end date
    if (!isBefore(actualPeriodEnd, adjustedEndDate)) {
      break;
    }
  }

  return periods;
};

// end of file
